import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Player as GistPlayer } from "@gistmed/gist-ui";
import { useRef, useState, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import NutritionalGuidelines from "../Instructions/NutritionalGuidelines";
import DontForget from "../Instructions/DontForget";
import Consent from "../Instructions/Consent";
import SatisfactionQuestions from "../Instructions/SatisfactionQuestions";
import Share from "../Share";
import Transition from "../Transition";
import { postAnalytics } from "../../../../../PatientComponents/analytics";
import useVideo from "../../../../hooks/useVideo";
import { usePatientFacing } from "../../../../flow/patientFacing/hooks/usePatientFacing";
import doctor2 from "../../../../assets/Mayo/Doctor2.png";
import GIPatientHeader from "../components/GIPatientHeader";
import arrow_up from "../../../../assets/gi/arrow_up.svg";
import { useLanguage } from "../../../../providers/LanguageProvider";
import { toVideoLanguage } from "../utils/videoLanguage";
const videoThumbnail = "/thumbnail2.png";

export default function GI2VideoPage1() {
  const { t } = useTranslation();
  const { getEventDataLocal } = usePatientFacing();
  const { lang: selectedLanguage } = useLanguage();
  const videoLanguage = toVideoLanguage(selectedLanguage);
  const event = getEventDataLocal();
  const caseId = event?.Case?.id;
  const caseIdRef = useRef(caseId);
  useEffect(() => {
    caseIdRef.current = caseId;
  }, [caseId]);
  const { ethnicity: rawEthnicity, age, gender } = event?.Case?.Avatar || {};
  const ethnicityMap = { white: "light", black: "dark" };
  const ethnicity = ethnicityMap[rawEthnicity] || rawEthnicity;

  const hasSentPostAnalytics = useRef(false);
  const videoRef = useRef(null);
  const videoSrcRef = useRef("");
  const [showFeedback, setShowFeedback] = useState(false);
  const [playerReady, setPlayerReady] = useState(false);

  const sendWatchedVideo = useCallback(() => {
    const currentCaseId = caseIdRef.current;
    if (!hasSentPostAnalytics.current) {
      hasSentPostAnalytics.current = true;
      console.log('[GI2] Sending watched-video analytics:', { caseId: currentCaseId, facingType: 'gi2' });
      postAnalytics(currentCaseId, "watched-video", undefined, "gi2");
    }
  }, []);

  const saveUserAction = useCallback((percentage) => {
    console.log('[GI2] saveUserAction:', { percentage, caseId: caseIdRef.current, hasSentPostAnalytics: hasSentPostAnalytics.current });
    if (percentage >= 75) sendWatchedVideo();
  }, [sendWatchedVideo]);

  const hasTriggeredFeedback = useRef(false);
  const hasSentVideoStarted = useRef(false);

  const handleTimeUpdate = useCallback((location) => {
    // Show feedback when video has actually progressed past 1 second
    console.log('[GI2] handleTimeUpdate called:', { location, hasTriggered: hasTriggeredFeedback.current });
    if (!hasTriggeredFeedback.current && location > 1) {
      hasTriggeredFeedback.current = true;
      console.log('[GI2] Video started - showing feedback');
      setShowFeedback(true);
    }
  }, [setShowFeedback]);

  const handleVideoStart = useCallback(() => {
    const currentCaseId = caseIdRef.current;
    if (!hasSentVideoStarted.current && currentCaseId) {
      hasSentVideoStarted.current = true;
      console.log('[GI2] Sending video-started analytics:', { caseId: currentCaseId, facingType: 'gi2' });
      postAnalytics(currentCaseId, "video-started", undefined, "gi2");
    }
    setShowFeedback(true);
  }, []);

  const handleLoadMetadata = useCallback(() => {
    if (!videoRef.current) return;
    const player = videoRef.current;
    const duration = player.duration ? player.duration() : 0;
    const currentTime = player.currentTime ? player.currentTime() : 0;
    if (duration > 0) {
      const pct = (currentTime / duration) * 100;
      console.log('[GI2] loadedmetadata - recalculating:', { pct, currentTime, duration });
      if (pct >= 75) sendWatchedVideo();
    }
  }, [sendWatchedVideo]);

  const handleAutoPlay = useCallback(() => {
    if (!videoRef.current) return;
    postAnalytics(caseIdRef.current, "back-to-top", undefined, "gi2");
    videoRef.current.play();
  }, []);

  // Get hospital name from localStorage and map "gi" to "UGI"
  const hospitalData = JSON.parse(localStorage.getItem("hospital") || "{}");
  const hospitalName = hospitalData.name?.toLowerCase() === "gi" ? "UGI" : "UGI";

  // Check query parameter for sending number (1 = first, 2 = second)
  // Also check event.facingType as fallback: 'gi' = sending 1, 'gi2' = sending 2
  const [searchParams] = useSearchParams();
  const querySending = searchParams.get("sending");
  const eventFacingType = event?.facingType;
  const sending = querySending || (eventFacingType === 'gi' ? '1' : '2');
  const projectName = sending === "1" ? "UGI Video 01" : "UGI Video 02";

  // Extract data from event
  const {
    bloodPressureMedicines,
    bloodThinners,
    diabetesMedicines,
    nutritionalSupplement,
    colonoscopyBefore,
    concentrate,
    procedureDate,
    procedureTime,
    location,
    personalNote,
    doctorName,
  } = event?.Case?.GiCase || {};
  
  // Use local time to avoid UTC timezone offset issues
  // Handle both date-only strings ("2026-06-03") and ISO strings ("2026-06-03T00:00:00.000Z")
  const safeDate = procedureDate 
    ? (typeof procedureDate === 'string' 
      ? new Date(procedureDate.split('T')[0] + 'T00:00:00') 
      : new Date(procedureDate))
    : null;
  const weekday = safeDate ? safeDate.toLocaleDateString('en-US', { weekday: 'long' }) : undefined;
  const time = procedureTime ? (parseInt(procedureTime.split(':')[0]) < 17 ? 'afternoon' : 'evening') : undefined;
  // Build questionnaire array based on user's medical answers
  const isYes = (val) => val === true || val === "Yes";
  const questionnaire = [];
  if (isYes(bloodThinners)) questionnaire.push("bloodThinners");
  if (isYes(diabetesMedicines)) questionnaire.push("diabetesMedicines");
  if (isYes(bloodPressureMedicines)) questionnaire.push("bloodPressureMedicines");
  if (isYes(nutritionalSupplement)) questionnaire.push("nutritionalSupplement");

  const data = {
    weekday,
    time,
    concentrate: concentrate ? concentrate.split(',')[0].toLowerCase() : undefined,
    questionnaire,
    doctorName,
  };

  const { video } = useVideo({
    language: videoLanguage,
    projectName: projectName,
    hospitalName: hospitalName,
    avatar: {
      age: age || "middle",
      gender: gender || "male",
      ethnicity: ethnicity || "light",
      hospital: "UGI",
      language: videoLanguage
    },
    data,
  });

  // Update videoSrcRef when video.src changes
  useEffect(() => {
    videoSrcRef.current = video?.src || "";
  }, [video?.src]);

  // Delay player rendering to prevent video.js focus error
  useEffect(() => {
    if (video?.src) {
      const timer = setTimeout(() => setPlayerReady(true), 500);
      return () => clearTimeout(timer);
    } else {
      setPlayerReady(false);
    }
  }, [video?.src]);

  console.log('GI2VideoPage1 - video.src:', video.src);
  console.log('GI2VideoPage1 - projectName:', projectName);
  console.log('GI2VideoPage1 - sending:', sending);

  return (
    <Transition>
      <Container>
        <Desktop>
          <GIPatientHeader padding="15px 30px" />
          <Title id="video-title">
            {t("GI2-Video-Title")}
          </Title>
          <VideoWrapper>
            {video.src && playerReady ? (
              <GistPlayer
                key={video.src}
                onLocationUpdate={saveUserAction}
                onPlayerPlaying={handleVideoStart}
                onLoadMetadata={handleLoadMetadata}
                onTimeUpdate={handleTimeUpdate}
                src={video.src}
                audioStartDelay={3}
                thumbnail={videoThumbnail}
                backgroundMusic="optimistic"
                showGistmdWaterMark={false}
                videoRef={videoRef}
              />
            ) : video.src ? (
              <div style={{ padding: '20px', textAlign: 'center' }}>
                <p>Loading video player...</p>
              </div>
            ) : (
              <div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>
                <p>Video not loading. Check console for details.</p>
              </div>
            )}
          </VideoWrapper>

          <VideoInteraction>
            <ShareWrapper>
              <Share />
            </ShareWrapper>
            <SatisfactionQuestions videoStarted={showFeedback} keyPrefix="GI2-" />
          </VideoInteraction>

          {/* Show procedure details for second sending */}
          {sending === "2" && procedureDate && (
            <ProcedureDetails>
              <ProcedureText>
                {t("Video-Procedure-Scheduled")}{" "}
                <strong>
                  {safeDate.toLocaleDateString(selectedLanguage || "en", {
                    weekday: "long",
                    day: "2-digit",
                    month: "2-digit",
                    year: "2-digit",
                  })}{" "}
                  {t("Video-Procedure-At")}{" "}
                  {(() => {
                    const time = procedureTime || "16:00";
                    const [hours, minutes] = time.split(":").map(Number);
                    const date = new Date();
                    date.setHours(hours, minutes, 0, 0);
                    return date.toLocaleTimeString(selectedLanguage || "en", {
                      hour: "numeric",
                      minute: "2-digit",
                      hour12: true,
                    });
                  })()}
                </strong>
              </ProcedureText>
              <ProcedureText>
                {t("Video-Arrival-Time")}{" "}
                <strong>
                  {(() => {
                    const time = procedureTime || "16:00";
                    const [hours, minutes] = time.split(":").map(Number);
                    const arrival = new Date();
                    arrival.setHours(hours, minutes - 45, 0, 0);
                    return arrival.toLocaleTimeString(selectedLanguage || "en", {
                      hour: "numeric",
                      minute: "2-digit",
                      hour12: true,
                    });
                  })()}
                </strong>
              </ProcedureText>
              <ProcedureText>
                {t("Video-Location")}{" "}
                <strong>{location || personalNote || t("Video-Default-Location")}</strong>
              </ProcedureText>
            </ProcedureDetails>
          )}

          <NutritionalGuidelines keyPrefix="GI2-" showDownloadButton />
          <DontForget keyPrefix="GI2-" />
          <Consent />

          <DoctorImage src={doctor2} alt="Doctor" />
          <ScrollButton href="#video-title" onClick={handleAutoPlay}>
            <img src={arrow_up} alt="arrowUp" />
            {t("Video-Back-To-Video")}
            <span style={{ width: "19px" }} />
          </ScrollButton>

          <Divider />
          <Footer>
            <span>{t("Footer").split(",")[0] + ","}</span>
            <span>{t("Footer").split(",").slice(1).join(",").trim()}</span>
          </Footer>
        </Desktop>
      </Container>
    </Transition>
  );
}

const Container = styled.div`
  --screen-margin: 23px;
  background: transparent
    linear-gradient(
      180deg,
      #ffffff 0%,
      #f1f4fb 10%,
      #e3e8f6 53%,
      #f5f7fc 85%,
      #ffffff 100%
    )
    0% 0% no-repeat padding-box;
  padding-block-end: 35px;
`;

const Desktop = styled.div`
  max-width: 800px;
  margin-inline: auto;
  font-family: "Abraham", sans-serif;
`;

const Title = styled.h1`
  margin-block-start: 2.125rem;
  margin-block-end: 1.813rem;
  text-align: start;
  font-family: "Abraham", sans-serif;
  font-size: 1.5rem;
  font-weight: 600;
  margin-inline: var(--screen-margin);
`;

const VideoWrapper = styled.div`
  margin-top: 24px;
  margin-bottom: 24px;
  width: calc(100% - 30px);
  margin-inline: auto;
  height: auto;
  display: block;
`;

const VideoInteraction = styled.div`
  display: flex;
  flex-direction: column;
  gap: 27px;
  margin-block: 8px 20px;
  margin-inline: var(--screen-margin);
`;

const ShareWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
`;

const ScrollButton = styled.a`
  display: flex;
  justify-content: space-between;
  text-decoration: none;
  text-align: center;
  font-size: 1.125rem;
  width: calc(100% - 50px);
  max-width: 12.25rem;
  cursor: pointer;
  color: #ffffff;
  background-color: #7a9dfd;
  border: none;
  padding-block: 0.688rem;
  padding-inline: 27px;
  border-radius: 50px;
  align-self: center;
  font-family: "Abraham", sans-serif;
  margin-inline: auto;
`;

const DoctorImage = styled.img`
  width: 160px;
  max-width: 100%;
  margin-block-end: 22px;
  margin-inline: auto;
  display: block;
`;

const Divider = styled.div`
  height: 1px;
  background-color: #84a4fc;
  margin-block-start: 35px;
  margin-block-end: 41px;
  margin-inline: var(--screen-margin);
  opacity: 0.2;
`;

const Footer = styled.footer`
  font-family: "Abraham", sans-serif;
  font-weight: 500;
  text-align: center;
  font-size: 1.375rem;
  padding-inline: 70px;
  span {
    display: block;
  }
`;

const ProcedureDetails = styled.div`
  margin: 24px var(--screen-margin);
  padding: 20px;
  background: #f8f9fa;
  border-radius: 12px;
  border-left: 4px solid #84a4fc;
`;

const ProcedureText = styled.p`
  font-family: "Abraham", sans-serif;
  font-size: 1.188rem;
  color: #1a1a1a;
  margin: 0 0 12px 0;
  line-height: 1.5;
  &:last-child {
    margin-bottom: 0;
  }
  strong {
    font-weight: 600;
    color: #333;
  }
`;
